import difference from 'lodash/difference';
import uniq from 'lodash/uniq';

import { assertString, assertUuid } from '../validators';
import uuid from '../video-data/utils/uuid';

import type { MixinBase, GetInstance, GetConstructorParameters } from '../types';
import type { RawVideoData } from '../video-data/types';
import { getTemplateValueAttribute, isTemplateAttribute } from './attributes';

export type SerializableMixinSettings = {
    /**
     * the unique uuid (generated automatically if omitted)
     */
    id?: string;
    /**
     *  the name to assign
     */
    name?: string;
};

export type LoadSettings = {
    skipTemplateAttributes?: boolean;
    onlyEditableProperties?: boolean;
};

export type SaveSettings = {
    clone?: boolean;
    returnTemplateReference?: boolean;
    skipTemplateAttributes?: boolean;
    onlyEditableProperties?: boolean;
};

/**
 * allProperties parameter for save method. A boolean will set all options
 */
export type AllProperties =
    | boolean
    | {
          /**
           * save readOnly properties
           * @default true
           */
          readOnly?: boolean;
          /**
           * save serializable properties
           * @default true
           */
          serializable?: boolean;
          /**
           * save editable properties
           * @default true
           */
          editable?: boolean;
      };

const defaultLoadPropertyListSettings: LoadSettings = {
    skipTemplateAttributes: false,
    onlyEditableProperties: false,
};

const defaultSavePropertyListSettings: SaveSettings = {
    clone: false,
    returnTemplateReference: true,
    skipTemplateAttributes: false,
    onlyEditableProperties: false,
};

export interface SerializableMixinInterface {
    new (): typeof SerializableMixin;
    _classType: string;
    _name: string;
    get classType(): string;
    get id(): string;
    get name(): string;
    set name(name: string);
    get readonlyProperties(): string[];
    get propertiesToSerialize(): Array<string>;
    get editableProperties(): Array<string>;
    generateNewId(): void;
    loadEditable(dataObj: Record<string, any>): void;
    saveEditable(): Record<string, any>;
    load(dataObj: Record<string, any>, settings?: LoadSettings): void;
    save(
        allProperties?: AllProperties,
        clone?: boolean,
        returnTemplateReference?: boolean,
        skipTemplateAttributes?: boolean,
    ): Record<string, any>;
    _savePropertyList(propertyKeys: Array<string>, settings?: SaveSettings): Record<string, any>;
}

/**
 * We support 3 different property load types
 */
type PropertyLoadType = 'customLoadFunction' | 'objectWithLoadMethod' | 'setter';

/**
 * Info about property, how we can serialize/deserialize it
 */
type PropertyInfo = {
    loadType?: PropertyLoadType;
    isTemplateAttribute: boolean;
};

/**
 * Mixin which handles all serializing (loading and saving) abilities of Luminary's
 * classes.
 * @deprecated use TypedSerializableMixin
 */
export const SerializableMixin = <TBase>(Base: MixinBase<TBase>) => {
    /**
     * Because our classes are not modified during runtime,
     * we can cache knowledge about particular class properties.
     * in order to avoid additional loolups in prototypes chain
     */
    const propertiesInfoForClass: Record<string, Record<string, PropertyInfo>> = {};

    const loadPropertiesListForClass: Record<string, string[]> = {};

    /**
     * @lends Luminary.SerializableMixin
     */
    const Derived = class extends (Base as any) {
        _classType = 'SerializableMixin';
        _name = 'SerializableMixin';

        #id = uuid();

        constructor(settings: SerializableMixinSettings) {
            // eslint-disable-next-line constructor-super
            super();

            if (!settings) {
                settings = {};
            }
            if (settings.hasOwnProperty('id') && settings.id !== undefined) {
                assertUuid(settings.id);
                this.#id = settings.id;
            }
            if (settings.hasOwnProperty('name') && settings.name !== undefined) {
                assertString(settings.name);
                this._name = settings.name;
            }
        }

        /**
         * The class type of the instance of this object.
         */
        get classType() {
            return this._classType;
        }

        /**
         * The object's unique identifier, a UUID, is used for lookup purposes. This ID must be unique to guarantee that
         * each ID retrieval operation returns a single asset within an entire video.
         *
         * Scene ids are generated (or can be assigned) upon scene creation. All assets
         * within a scene will copy their id from the scene design and then generate a new id.
         *
         * Once an id has been assigned it will persist. This means loading and saving
         * will not cause new slide ids or asset ids to be generated. EXCEPT in one case, partials.
         *
         * Since partials are not explicitly serialized in video data, we cannot remember the
         * asset ids for them. This means loading & saving and changing aspect ratios will
         * generate new ids for assets specified by partials. This should be okay, since
         * partials have their own dedicated api (e.g. scene.attribution) and should not be
         * interacted with through the getAssets api.
         */
        get id() {
            return this.#id;
        }

        /**
         * The name used to identify this object instance
         */
        get name() {
            return this._name;
        }

        /**
         * Sets the name used to identify this object instance.
         *
         * This should only be used from designer editors. If the name is set to anything else than a `BaseAsset` child,
         * an update will not be emitted. If an update is requires, see `BaseAsset` overrides.
         */
        set name(name: string) {
            this._name = name;
        }

        /**
         * The list of read-only properties. All of these properties can only be set during
         * initialization, afterwards they can only be read by the api. These will be included in
         * the serialized output, but are not loaded with load. These properties can be thought
         * of as system properties or ones that users of the api should never change, for
         * example the unique id.
         */
        get readonlyProperties() {
            return ['classType', 'id'];
        }

        /**
         * The list of properties which specifies editable properties, which will not be
         * preserved when changing the aspect ratio, but are always loaded directly. If a
         * property is defined in editableProperties, adding it here will have no further effect.
         */
        get propertiesToSerialize(): Array<string> {
            return ['name'];
        }

        /**
         * The list of properties which are editable by the end-user.
         * These properties will be preserved when changing aspect ratios.
         * Properties defined here do not need to be added to propertiesToSerialize (they are
         * implicitly included in saves and loads).
         */
        get editableProperties(): Array<string> {
            return [];
        }

        /**
         * Generates and sets a new id (a uuid) for the object
         */
        generateNewId() {
            this.#id = uuid();
        }

        /**
         * Load only the editable properties. Refer to load for further documentation.
         * @param dataObj - Object containing property and value key pairs
         */
        loadEditable(dataObj: Record<string, any>) {
            this.#loadPropertyList(dataObj, this.editableProperties, {
                onlyEditableProperties: true,
            });
        }

        /**
         * Save all editable properties. Refer to save for further documentation.
         */
        _saveEditableProperties(settings?: SaveSettings): Record<string, any> {
            // when re-loading these properties we require an identifier to select which object a
            // given property should be loaded to, currently using 'name' for that
            // but we'll want to switch to using id eventually
            const propertyKeys = [...this.editableProperties, 'name'];
            return this._savePropertyList(propertyKeys, settings);
        }

        // TODO: `save()` should take `SaveSettings` and then `onlyEditableProperties` can be set to true, and this
        // function becomes deprecated.
        saveEditable(): Record<string, any> {
            return this._saveEditableProperties({
                ...defaultSavePropertyListSettings,
                onlyEditableProperties: true,
            });
        }

        /**
         * Loads all property values except the read-only properties.
         *
         * Note that a class can define custom load logic by adding a class function called
         * `_{property}CustomLoad`, the custom load takes highest precedence.
         * If no custom load exists then load will check to see if the property on the class is also
         * a serializable object which defines a load method, if so then it will call load for that
         * class.
         * Finally, if neither of those criteria were met, load will try to simply assign the value
         * to the class.
         */
        load(dataObj: RawVideoData | Record<string, any>, settings?: LoadSettings) {
            if (!loadPropertiesListForClass[this.classType]) {
                loadPropertiesListForClass[this.classType] = difference(
                    uniq(this.propertiesToSerialize.concat(this.editableProperties)),
                    this.readonlyProperties,
                );
            }
            this.#loadPropertyList(
                dataObj,
                loadPropertiesListForClass[this.classType],
                settings as LoadSettings,
            );
        }

        /**
         * Save all properties and their values
         *
         * Note that a class can define custom save logic by adding a class function called
         * `_{property}CustomSave`, the custom save takes highest precedence.
         * If no custom save exists then save will check to see if the property on the class is also
         * a serializable object which defines a save method, if so then it will call save for that
         * class.
         * Finally, if neither of those criteria were met, save will try to simply assign the value
         * to the class.
         * serialized representation of all properties
         */
        save(
            allProperties: AllProperties = true,
            clone = false,
            returnTemplateReference = true,
            skipTemplateAttributes = false,
        ) {
            let readOnly = false;
            let serializable = false;
            let editable = false;
            if (typeof allProperties === 'boolean') {
                readOnly = true;
                serializable = true;
                editable = true;
            } else {
                readOnly = allProperties.readOnly ?? false;
                serializable = allProperties.serializable ?? false;
                editable = allProperties.editable ?? false;
            }

            let properties: string[] = [];
            if (readOnly) {
                properties = properties.concat(this.readonlyProperties);
            }
            if (serializable) {
                properties = properties.concat(this.propertiesToSerialize);
            }
            if (editable) {
                properties = properties.concat(this.editableProperties);
            }
            properties = uniq(properties);

            return this._savePropertyList(properties, {
                clone,
                returnTemplateReference,
                skipTemplateAttributes,
                // This flag is true when we are only wanting to save editabl properties, and not the others.
                onlyEditableProperties: editable && !serializable && !readOnly,
            });
        }

        /**
         * Loads the properties, which are specified in the property list from the dataObj into the
         * instance of this class.
         */
        #loadPropertyList(
            dataObj: Record<string, any>,
            propertyList: Array<string>,
            settings?: LoadSettings,
        ) {
            settings = {
                ...defaultLoadPropertyListSettings,
                ...settings,
            };

            let propertiesInfo = propertiesInfoForClass[this.classType];
            if (!propertiesInfo) {
                propertiesInfo = {};
                propertiesInfoForClass[this.classType] = propertiesInfo;
            }

            propertyList.forEach((propertyName) => {
                if (!dataObj.hasOwnProperty(propertyName)) {
                    return;
                }

                let propertyInfo = propertiesInfo[propertyName];

                if (!propertyInfo) {
                    let propertyLoadType: PropertyLoadType;
                    const customLoadName = `_${propertyName}CustomLoad`;
                    if (customLoadName in this) {
                        propertyLoadType = 'customLoadFunction';
                    } else if (
                        !!this[propertyName] &&
                        typeof this[propertyName] === 'object' &&
                        typeof this[propertyName].load === 'function'
                    ) {
                        propertyLoadType = 'objectWithLoadMethod';
                    } else {
                        propertyLoadType = 'setter';
                    }

                    propertyInfo = {
                        loadType: propertyLoadType,
                        isTemplateAttribute: isTemplateAttribute(this, propertyName),
                    };

                    propertiesInfo[propertyName] = propertyInfo;
                }

                if (settings.skipTemplateAttributes && propertyInfo.isTemplateAttribute) {
                    return;
                }

                switch (propertyInfo.loadType) {
                    case 'customLoadFunction':
                        const customLoadName = `_${propertyName}CustomLoad`;
                        this[customLoadName](dataObj[propertyName], settings);
                        break;
                    case 'objectWithLoadMethod':
                        this[propertyName].load(dataObj[propertyName], settings);
                        break;
                    case 'setter':
                        if (
                            Array.isArray(dataObj[propertyName]) &&
                            dataObj[propertyName].some(
                                (obj: any) =>
                                    typeof obj === 'object' && obj.hasOwnProperty('classType'),
                            )
                        ) {
                            throw new Error(
                                `Loading an array of serialized objects requires a custom loader for ${this.name}, ${this.classType}`,
                            );
                        }

                        this[propertyName] = dataObj[propertyName];
                        break;
                }
            });
        }

        /**
         * Test if this is an array of primitive or non serializable/editable values.
         * This is to prevent indirect cloning via `map` when checking for serializable/editable when saving an array.
         */
        #isSimpleArray(array: any[]): boolean {
            return array.every((element) => {
                if (!element || typeof element !== 'object') {
                    return true;
                }

                if (Array.isArray(element)) {
                    return this.#isSimpleArray(element);
                }

                if (
                    typeof element.save === 'function' ||
                    typeof element._saveEditableProperties === 'function'
                ) {
                    return false;
                }

                return true;
            });
        }

        /**
         * Saves the properties, which are specified in the property list into a JS object
         * @internal
         */
        _savePropertyList(propertyKeys: readonly string[], settings?: SaveSettings) {
            settings = {
                ...defaultSavePropertyListSettings,
                ...settings,
            };
            const skipTemplateAttributes = settings.skipTemplateAttributes;
            const returnTemplateReference = settings.returnTemplateReference;
            const clone = settings.clone;
            const onlyEditableProperties = settings.onlyEditableProperties;

            return Object.fromEntries(
                propertyKeys
                    .filter((propertyKey) => {
                        if (skipTemplateAttributes) {
                            return !isTemplateAttribute(this, propertyKey);
                        }
                        return true;
                    })
                    .map((propertyKey) => {
                        // A template attribute returns its template reference when calling _customSave and the resolved value
                        // when calling the getter. In certain case we want one or the other.
                        if (returnTemplateReference && isTemplateAttribute(this, propertyKey)) {
                            return [propertyKey, getTemplateValueAttribute(this, propertyKey)];
                        }

                        const customSave = `_${propertyKey}CustomSave`;
                        if (customSave in this) {
                            // The customSave references an object
                            const customSaveResult = this[customSave](
                                true,
                                clone,
                                returnTemplateReference,
                                skipTemplateAttributes,
                            );
                            if (clone && typeof customSaveResult === 'object') {
                                return [propertyKey, JSON.parse(JSON.stringify(customSaveResult))];
                            }
                            return [propertyKey, customSaveResult];
                        }

                        const propertyValue = this[propertyKey];

                        // Property value is null/undefined/0 or a primitive value.
                        if (!propertyValue || typeof propertyValue !== 'object') {
                            return [propertyKey, propertyValue];
                        }

                        // The array elements might be serializable.
                        if (Array.isArray(propertyValue) && !this.#isSimpleArray(propertyValue)) {
                            return [
                                propertyKey,
                                propertyValue.map((element) => {
                                    if (!element || typeof element !== 'object') {
                                        return element;
                                    }
                                    if (
                                        !onlyEditableProperties &&
                                        typeof element.save === 'function'
                                    ) {
                                        return element.save(
                                            true,
                                            clone,
                                            returnTemplateReference,
                                            skipTemplateAttributes,
                                        );
                                    }
                                    if (
                                        onlyEditableProperties &&
                                        typeof element._saveEditableProperties === 'function'
                                    ) {
                                        return element._saveEditableProperties(settings);
                                    }
                                    if (clone && typeof element.save === 'undefined') {
                                        return JSON.parse(JSON.stringify(element));
                                    }
                                    return element;
                                }),
                            ];
                        }

                        // The property references another serializable class instance
                        if (!onlyEditableProperties && typeof propertyValue.save === 'function') {
                            return [
                                propertyKey,
                                propertyValue.save(
                                    true,
                                    clone,
                                    returnTemplateReference,
                                    skipTemplateAttributes,
                                ),
                            ];
                        }

                        // We actually only want the editable properties.
                        if (
                            onlyEditableProperties &&
                            typeof propertyValue._saveEditableProperties === 'function'
                        ) {
                            return [propertyKey, propertyValue._saveEditableProperties(settings)];
                        }

                        // The property references an object
                        if (clone && typeof propertyValue.save === 'undefined') {
                            // while writing this, we tested both JSON and cloneDeep and found the former to be more performant
                            return [propertyKey, JSON.parse(JSON.stringify(propertyValue))];
                        }

                        // Simple save
                        return [propertyKey, propertyValue];
                    }),
            );
        }
    };

    // Explicitly say that this mixin will not pass parameters to next constructor
    return Derived as new (
        props: GetConstructorParameters<typeof Derived>,
    ) => TBase & GetInstance<typeof Derived>;
};

/**
 * Mixin which handles all serializing (loading and saving) abilities of Luminary's
 * classes.
 */
export const TypedSerializableMixin = function <Base>(base: Base) {
    /**
     * @lends Luminary.SerializableMixin
     */
    const Derived = SerializableMixin(base as any);

    type ReturnType = SerializableMixinInterface & GetInstance<Base>;

    return Derived as unknown as new (
        props: GetConstructorParameters<typeof Derived>,
    ) => ReturnType;
};
